<script lang="ts">
  import Search from '@lucide/svelte/icons/search'
  import Loader2 from '@lucide/svelte/icons/loader-2'
  import BookOpen from '@lucide/svelte/icons/book-open'
  import User from '@lucide/svelte/icons/user'
  import Library from '@lucide/svelte/icons/library'
  import Users from '@lucide/svelte/icons/users'
  import { SOURCE_LABELS } from '@/utils/searchSources'
  import type {
    ExternalBook,
    SearchHit,
    SearchResultItem,
  } from '@/utils/searchRanking'

  let {
    items,
    layout,
    isSearchingBooks,
    error = '',
    onSelectHit,
    onSelectExternal,
  }: {
    items: SearchResultItem[]
    layout: 'desktop' | 'mobile'
    isSearchingBooks: boolean
    error?: string
    onSelectHit: (hit: SearchHit) => void
    onSelectExternal: (book: ExternalBook) => void
  } = $props()

  function getIcon(recordType: SearchHit['record_type']) {
    switch (recordType) {
      case 'book':
        return BookOpen
      case 'author':
      case 'user':
        return User
      case 'series':
        return Library
      case 'club':
        return Users
      default:
        return Search
    }
  }

  function getTypeLabel(recordType: SearchHit['record_type']) {
    switch (recordType) {
      case 'book':
        return 'Book'
      case 'author':
        return 'Author'
      case 'series':
        return 'Series'
      case 'user':
        return 'User'
      case 'club':
        return 'Club'
    }
  }

  const rowClass = $derived(
    layout === 'mobile'
      ? 'w-full flex items-center gap-3 p-4 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors text-left'
      : 'w-full text-left p-3 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors border-b border-gray-100 dark:border-gray-700 last:border-b-0 flex items-start gap-3'
  )
  const placeholderClass = $derived(
    layout === 'mobile'
      ? 'w-10 h-14 bg-gray-200 dark:bg-gray-700 rounded flex items-center justify-center shrink-0'
      : 'w-10 h-14 bg-gray-200 dark:bg-gray-600 rounded flex items-center justify-center shrink-0'
  )
</script>

{#if error}
  <p role="alert" class="p-4 text-danger-600 dark:text-danger-400 text-sm">
    {error}
  </p>
{/if}

{#each items as item (item.kind === 'algolia' ? item.hit.objectID : 'ext-' + item.book.external_id)}
  {#if item.kind === 'algolia'}
    {@const hit = item.hit}
    {@const Icon = getIcon(hit.record_type)}
    <button type="button" onclick={() => onSelectHit(hit)} class={rowClass}>
      {#if hit.image_url}
        <img
          src={hit.image_url}
          alt={hit.title}
          class="w-10 h-14 object-cover rounded shrink-0"
        />
      {:else}
        <div class={placeholderClass}>
          <Icon size={20} class="text-gray-400" />
        </div>
      {/if}
      <div class="min-w-0 flex-1">
        <span
          class="text-xs font-medium px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400"
        >
          {getTypeLabel(hit.record_type)}
        </span>
        <p class="font-medium text-gray-900 dark:text-white truncate mt-1">
          {hit.title}
        </p>
        {#if hit.subtitle}
          <p class="text-sm text-gray-500 dark:text-gray-400 truncate">
            {hit.subtitle}
          </p>
        {/if}
      </div>
    </button>
  {:else}
    {@const book = item.book}
    <button
      type="button"
      onclick={() => onSelectExternal(book)}
      class={rowClass}
    >
      {#if book.cover_url}
        <img
          src={book.cover_url}
          alt={book.title}
          class="w-10 h-14 object-cover rounded shrink-0"
        />
      {:else}
        <div class={placeholderClass}>
          <BookOpen size={20} class="text-gray-400" />
        </div>
      {/if}
      <div class="min-w-0 flex-1">
        <span
          class="text-xs font-medium px-1.5 py-0.5 rounded {SOURCE_LABELS
            .google_books.color}"
        >
          {SOURCE_LABELS.google_books.label}
        </span>
        <p class="font-medium text-gray-900 dark:text-white truncate mt-1">
          {book.title}
        </p>
        {#if book.author}
          <p class="text-sm text-gray-500 dark:text-gray-400 truncate">
            {book.author}
          </p>
        {/if}
      </div>
    </button>
  {/if}
{/each}

{#if isSearchingBooks}
  <div
    class="flex items-center justify-center gap-2 {layout === 'mobile'
      ? 'p-4'
      : 'p-3'} text-gray-400"
  >
    <Loader2 size={layout === 'mobile' ? 16 : 14} class="animate-spin" />
    <span class={layout === 'mobile' ? 'text-sm' : 'text-xs'}
      >Searching Google Books…</span
    >
  </div>
{/if}
